<template>
  <div class="mx-auto -mt-8 max-w-[var(--breakpoint-xl)] sm:-mt-16">
    <UTabs
      v-model="activeTab"
      :items="floatingNav"
      :unmount-on-hide="false"
      :ui="{
        list: 'bg-neutral-900/40 ring ring-yellow-400 backdrop-blur-lg max-w-2xl z-[10] max-auto sticky top-28 lg:top-40',
        label: 'font-bold',
        leadingIcon: 'hidden sm:block',
        trigger: 'disabled:!text-neutral-600 disabled:text-xs',
        content: 'px-4 sm:px-6',
      }"
      class="px-2"
      @update:model-value="updateRouteParams"
    >
      <template #overview>
        <LazyMarketingTabsProductsOverview
          v-if="type !== 'package'"
          :blok="typedBlok"
          :uuid="uuid"
          :type="type"
          :name="name"
          :activities-count="activitiesCount"
          :gifts-count="giftsCount"
          :events-count="eventsCount"
          :packages-count="packagesCount"
          @no-rsvp="removeItemBySlot('availability')"
        />
        <LazyMarketingTabsProductsPackage
          v-else
          :blok="blok as PackageStoryblok"
          :uuid="uuid"
          :type="type"
        />
      </template>
      <template #activities>
        <LazyMarketingTabsProductsActivities
          :blok="blok"
          :uuid="uuid"
          :type="type"
          :name="name"
          @packages-count="updatePackagesCount"
          @activities-count="updateActivitiesCount"
        />
      </template>
      <template #availability>
        <LazyMarketingTabsProductsProducts
          :blok="blok as GroupStoryblok"
          :uuid="uuid"
          :type="type"
          :name="name"
          :locations="locations"
        />
      </template>
      <template #packages>
        <LazyMarketingTabsProductsPackages
          :blok="blok as PackageStoryblok"
          :uuid="uuid"
          :type="type"
          :name="name"
          @packages-false="removeItemBySlot('packages')"
          @packages-count="updatePackagesCount"
        />
      </template>
      <template #events>
        <LazyMarketingTabsProductsEvents
          :blok="blok as EventStoryblok"
          :uuid="uuid"
          :type="eventType"
          :name="name"
          @events-false="removeItemBySlot('events')"
          @events-count="updateEventsCount"
        />
      </template>
      <template #gifts>
        <LazyMarketingTabsProductsGifts
          :blok="blok"
          :uuid="uuid"
          :type="type"
          :name="name"
          :enable-gift-redemption="false"
          @gifts-false="removeItemBySlot('gifts')"
          @gifts-count="updateGiftsCount"
        />
      </template>
    </UTabs>
  </div>
</template>

<script setup lang="ts">
import type {
  ActivityStoryblok,
  LocationStoryblok,
  PackageStoryblok,
  EventStoryblok,
  GroupStoryblok,
} from '~/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/js';
import type { TabsItem } from '#ui/types';

const { fetchLocations } = useStoryblokClient();
const route = useRoute();
const router = useRouter();

// Check if route includes climbing-centre
const isClimbingCentre = computed(() => route.path.includes('climbing-centre'));

type ProductType = 'location' | 'activity' | 'package' | 'event' | 'group';

interface Props {
  blok:
    | ActivityStoryblok
    | LocationStoryblok
    | PackageStoryblok
    | EventStoryblok
    | GroupStoryblok;
  uuid: string;
  type: ProductType;
  name: string;
}

const props = defineProps<Props>();

// FLoating Nav Values
interface NavItem extends TabsItem {
  slot: string;
  disabled: boolean;
}

// Base navigation arrays
const baseLocationNav = [
  {
    label: 'Info',
    icon: 'i-lucide-info',
    slot: 'overview',
    disabled: false,
  },
  {
    label: 'Activities',
    icon: 'i-lucide-zap',
    slot: 'activities',
    disabled: false,
  },
  {
    label: 'Events',
    icon: 'i-lucide-calendar-days',
    slot: 'events',
    disabled: false,
  },
  {
    label: 'Gifts',
    icon: 'i-lucide-gift',
    slot: 'gifts',
    disabled: false,
  },
];

// Computed navigation arrays
const locationNav = computed(() => {
  return baseLocationNav.map((item) => {
    if (item.slot === 'activities') {
      return {
        ...item,
        label: isClimbingCentre.value ? 'Sessions' : 'Activities',
      };
    }
    return item;
  });
});

const floatingNav = ref<NavItem[]>([]);

// Function to remove a tab by slot
const removeItemBySlot = (slotToRemove: string) => {
  floatingNav.value = floatingNav.value.filter(
    (item) => item.slot !== slotToRemove
  );
};

// Navigation arrays with disabled property
const activityNav = ref<NavItem[]>([
  {
    label: 'Info',
    icon: 'i-lucide-info',
    slot: 'overview',
    disabled: false,
  },
  {
    label: 'Prices',
    icon: 'i-lucide-pound-sterling',
    slot: 'availability',
    disabled: false,
  },
  {
    label: 'Packages',
    icon: 'i-lucide-ticket-percent',
    slot: 'packages',
    disabled: false,
  },
  {
    label: 'Events',
    icon: 'i-lucide-calendar-days',
    slot: 'events',
    disabled: false,
  },
  {
    label: 'Gifts',
    icon: 'i-lucide-gift',
    slot: 'gifts',
    disabled: false,
  },
]);

const packageNav = ref<NavItem[]>([
  {
    label: 'Info',
    icon: 'i-lucide-info',
    slot: 'overview',
    disabled: false,
  },
  {
    label: 'Book Now',
    icon: 'i-lucide-pound-sterling',
    slot: 'availability',
    disabled: false,
  },
]);

const eventNav = ref<NavItem[]>([
  {
    label: 'Info',
    icon: 'i-lucide-info',
    slot: 'overview',
    disabled: false,
  },
  {
    label: 'Activities',
    icon: 'i-lucide-zap',
    slot: 'activities',
    disabled: false,
  },
  {
    label: 'RSVP',
    icon: 'i-lucide-ticket',
    slot: 'availability',
    disabled: false,
  },
]);

const groupNav = ref<NavItem[]>([
  {
    label: 'Info',
    icon: 'i-lucide-info',
    slot: 'overview',
    disabled: false,
  },
  {
    label: 'Activities',
    icon: 'i-lucide-zap',
    slot: 'activities',
    disabled: false,
  },
  {
    label: 'Packages',
    icon: 'i-lucide-ticket-percent',
    slot: 'availability',
    disabled: false,
  },
]);

// Set Floating Nav Values
if (props.type === 'location') {
  floatingNav.value = locationNav.value;
} else if (props.type === 'activity') {
  floatingNav.value = activityNav.value;
  // Remove 'availability' tab if activity_types is 'corporates'
  if (
    'activity_types' in props.blok &&
    props.blok.activity_types.includes('corporate')
  ) {
    removeItemBySlot('availability');
  }
} else if (props.type === 'package') {
  floatingNav.value = packageNav.value;
} else if (props.type === 'event') {
  floatingNav.value = eventNav.value;
} else if (props.type === 'group') {
  if (route.path.includes('/kids/') || route.path.includes('/ace/')) {
    // Filter out the Activities tab for kids and ace routes
    floatingNav.value = groupNav.value.filter(
      (item) => item.label !== 'Activities'
    );
  } else {
    floatingNav.value = groupNav.value;
  }
}

const queryParam = route.query.tab as string | undefined;
const activeTab = ref<string>('0');

// Set Active Tab
onMounted(() => {
  if (props.type === 'location') {
    if (queryParam === 'activities') {
      activeTab.value = '1';
    } else if (queryParam === 'events') {
      activeTab.value = '2';
    } else if (queryParam === 'gifts') {
      activeTab.value = '3';
    }
  } else if (props.type === 'activity') {
    if (queryParam === 'availability') {
      activeTab.value = '1';
    } else if (queryParam === 'packages') {
      activeTab.value = '2';
    } else if (queryParam === 'events') {
      activeTab.value = '3';
    } else if (queryParam === 'gifts') {
      activeTab.value = '4';
    }
  } else if (props.type === 'package') {
    if (queryParam === 'availability') {
      activeTab.value = '1';
    }
  } else if (props.type === 'event') {
    if (queryParam === 'activities') {
      activeTab.value = '1';
    } else if (queryParam === 'rsvp') {
      activeTab.value = '2';
    }
  } else if (props.type === 'group') {
    if (route.path.includes('/kids/')) {
      if (queryParam === 'packages') {
        activeTab.value = '1';
      }
    } else {
      if (queryParam === 'activities') {
        activeTab.value = '1';
      } else if (queryParam === 'packages') {
        activeTab.value = '2';
      }
    }
  }
});

// Update Query Params
const updateRouteParams = (newTabValue: string | number) => {
  let newTabQuery = '';

  if (props.type === 'location') {
    if (newTabValue === '1') {
      newTabQuery = 'activities';
    } else if (newTabValue === '2') {
      newTabQuery = 'events';
    } else if (newTabValue === '3') {
      newTabQuery = 'gifts';
    } else {
      newTabQuery = 'info';
    }
  } else if (props.type === 'activity') {
    if (newTabValue === '1') {
      newTabQuery = 'availability';
    } else if (newTabValue === '2') {
      newTabQuery = 'packages';
    } else if (newTabValue === '3') {
      newTabQuery = 'events';
    } else if (newTabValue === '4') {
      newTabQuery = 'gifts';
    } else {
      newTabQuery = 'info';
    }
  } else if (props.type === 'package') {
    if (newTabValue === '1') {
      newTabQuery = 'availability';
    } else {
      newTabQuery = 'info';
    }
  } else if (props.type === 'event') {
    if (newTabValue === '1') {
      newTabQuery = 'activities';
    } else if (newTabValue === '2') {
      newTabQuery = 'rsvp';
    } else {
      newTabQuery = 'info';
    }
  } else if (props.type === 'group') {
    if (route.path.includes('/kids/')) {
      if (newTabValue === '1') {
        newTabQuery = 'packages';
      } else {
        newTabQuery = 'info';
      }
    } else {
      if (newTabValue === '1') {
        newTabQuery = 'activities';
      } else if (newTabValue === '2') {
        newTabQuery = 'packages';
      } else {
        newTabQuery = 'info';
      }
    }
  }

  // Update the route query parameter without reloading the page
  router.replace({
    query: {
      ...route.query, // Keep other query params
      tab: newTabQuery,
    },
  });

  // Scroll to the top of the section
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// Watch for changes in query parameters
watch(
  () => route.query.tab,
  (newTabQuery) => {
    // Perform any actions needed when the query parameter changes
    if (props.type === 'location') {
      if (newTabQuery === 'activities') {
        activeTab.value = '1';
      } else if (newTabQuery === 'events') {
        activeTab.value = '2';
      } else if (newTabQuery === 'gifts') {
        activeTab.value = '3';
      }
    } else if (props.type === 'activity') {
      if (newTabQuery === 'availability') {
        activeTab.value = '1';
      } else if (newTabQuery === 'packages') {
        activeTab.value = '2';
      } else if (newTabQuery === 'events') {
        activeTab.value = '3';
      } else if (newTabQuery === 'gifts') {
        activeTab.value = '4';
      }
    } else if (props.type === 'package') {
      if (newTabQuery === 'availability') {
        activeTab.value = '1';
      }
    } else if (props.type === 'event') {
      if (newTabQuery === 'activities') {
        activeTab.value = '1';
      } else if (newTabQuery === 'rsvp') {
        activeTab.value = '2';
      }
    } else if (props.type === 'group') {
      if (route.path.includes('/kids/')) {
        if (newTabQuery === 'packages') {
          activeTab.value = '1';
        }
      } else {
        if (newTabQuery === 'activities') {
          activeTab.value = '1';
        } else if (newTabQuery === 'packages') {
          activeTab.value = '2';
        }
      }
    }
  }
);

// Fetch Locations
const locations = ref<ISbStoryData<LocationStoryblok>[]>([]);

if (props.type !== 'location') {
  // Load locations and set them in the reactive `locations` array
  const loadLocations = async () => {
    try {
      const [stories] = await Promise.all([fetchLocations()]);
      locations.value = stories as unknown as ISbStoryData<LocationStoryblok>[];
    } catch (error) {
      console.error(error);
    }
  };

  await loadLocations();
}

// Set activitiesCount to 0 by default
const activitiesCount = ref<number>(0);

// Update activitiesCount when the number of activities changes
const updateActivitiesCount = (count: number) => {
  activitiesCount.value = count;
};

const packagesCount = ref<number>(0);

const updatePackagesCount = (count: number) => {
  packagesCount.value = count;
};

// Set giftsCount to 0 by default
const giftsCount = ref<number>(0);

// Update giftsCount when the number of gifts changes
const updateGiftsCount = (count: number) => {
  giftsCount.value = count;
};

// Set eventsCount to 0 by default
const eventsCount = ref<number>(0);

// Update eventsCount when the number of events changes
const updateEventsCount = (count: number) => {
  eventsCount.value = count;
};

const eventType = computed(() => props.type as 'location' | 'activity');

const typedBlok = computed(
  () =>
    props.blok as
      | ActivityStoryblok
      | LocationStoryblok
      | EventStoryblok
      | GroupStoryblok
);
</script>
